/* oxlint-disable no-inner-declarations */
import { ok } from '@oazapfts/runtime';
import * as rq from '@tanstack/react-query';
import { reject } from 'lodash';
import { z } from 'zod';

import { FeApi, useInjectFeApiContext } from '@/api/fe-api';
import type { CustomerConfig } from '@/entities/customer-config';
import {
  type FeatureFlagChecker,
  useFeatureFlag,
} from '@/hooks/use-feature-flag';
import type { Filters as ViewFilters } from '@/modules/system-logs/view/state/selected-filters';
import { FiltersSchema as ServerFiltersSchema } from '@/system-logs/entities/filters';
import { getUnitFromFilter } from '@/system-logs/utils/get-unit-from-filter';

/**
 * GetFiltersQuery (stub)
 * - Mirrors warranty-v3 pattern: server returns a bundle of filter definitions
 * - Currently generates deterministic, local data to keep FE unblocked
 * - Prepares for a drop-in switch to the real BE endpoint once available
 */
export namespace GetFiltersQuery {
  // Deep serialization for React Query cache keys
  const serializeForCacheKey = (obj: any): any[] => {
    if (obj === null || obj === undefined) return [obj];
    if (typeof obj !== 'object') return [obj];
    if (Array.isArray(obj)) return obj.flatMap(serializeForCacheKey);

    // Handle Date objects
    if (obj instanceof Date) return [obj.toISOString()];

    // Handle nested objects
    return Object.entries(obj)
      .sort(([a], [b]) => a.localeCompare(b)) // Sort keys for consistent ordering
      .flatMap(([key, value]) => [key, ...serializeForCacheKey(value)]);
  };

  export const key = (input: Input) => [
    'system-logs',
    'filters',
    ...serializeForCacheKey(input),
  ];

  // Restrict certain domains to their respective feature flags
  const DOMAIN_FILTER_MAP: Record<
    string,
    keyof CustomerConfig['featureFlags']
  > = {
    pcs: 'pcs',
  };

  export type Input = {
    assetId?: string; // optional for now; real API will likely require it
    filters: ViewFilters; // current URL-driven selections (used for dependency shaping)
  };

  export const OutputSchema = z.object({
    filters: ServerFiltersSchema,
  });
  export type Output = z.infer<typeof OutputSchema>;

  function normalizeResponse(
    data: FeApi.ListFiltersViewOutput,
    featureFlag: FeatureFlagChecker,
  ) {
    const domain = data.domain
      ? {
          ...data.domain,
          values: reject(data.domain.values, ({ id }) => {
            const requiredFlag = DOMAIN_FILTER_MAP[id];
            return requiredFlag && !featureFlag(requiredFlag);
          }),
        }
      : undefined;

    return {
      coreFilters: {
        dateRange: {
          from: data.start_date?.values?.[0]
            ? new Date(data.start_date.values[0] as unknown as string)
            : undefined,
          to: data.end_date?.values?.[0]
            ? new Date(data.end_date.values[0] as unknown as string)
            : undefined,
        },
        domain,
        siteId: data.site,
      },
      batteryFilters: {
        array: data.array,
        node: data.node,
        container: data.container,
        bank: data.bank,
        pack: data.pack,
        module: data.module,
      },
      pcsFilters: {
        pcs: data.pcs,
      },
    };
  }

  function useQueryFn() {
    const featureFlag = useFeatureFlag();
    const systemLogsFiltersList = useInjectFeApiContext(
      FeApi.systemLogsV1FiltersListRetrieve,
    );

    return async function queryFn(input: Input): Promise<Output> {
      const siteAccureId = input.assetId ?? input.filters?.coreFilters.siteId;
      const unitAccureId = getUnitFromFilter(input.filters).accureId;
      const domain = input.filters.coreFilters.domain as
        | 'battery'
        | 'pcs'
        | 'other';
      const result = await ok(
        systemLogsFiltersList({
          siteAccureId,
          unitAccureId,
          domain,
        }),
      );
      const normalized = normalizeResponse(result, featureFlag);
      const output = await OutputSchema.safeParseAsync({
        filters: normalized,
      });
      if (!output.success) {
        throw new Error('Failed to build system-logs filters data', {
          cause: output.error,
        });
      }
      return output.data;
    };
  }

  export function useQuery(input: Input) {
    const queryFn = useQueryFn();
    return rq.useQuery<Output>({
      queryKey: key(input),
      queryFn: () => queryFn(input),
      keepPreviousData: true,
      staleTime: 15_000,
    });
  }

  export function usePrefetch() {
    const queryClient = rq.useQueryClient();
    const queryFn = useQueryFn();
    return async function prefetch(input: Input): Promise<void> {
      await queryClient.prefetchQuery<Output>({
        queryKey: key(input),
        queryFn: () => queryFn(input),
      });
    };
  }
}
